import { useEffect, useMemo, useRef, useState } from "react";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
  BarChart, Bar,
} from "recharts";

// Only mounts children after its own container has non-zero size — silences the
// Recharts "-1,-1" warning when the chart lives inside a Radix TabsContent that
// starts as display:none.
function ChartMount({ children, className }) {
  const ref = useRef(null);
  const [ready, setReady] = useState(false);
  useEffect(() => {
    if (!ref.current) return;
    const check = () => {
      const r = ref.current?.getBoundingClientRect();
      if (r && r.width > 0 && r.height > 0) {
        setReady(true);
        return true;
      }
      return false;
    };
    if (check()) return;
    const ro = new ResizeObserver(() => check());
    ro.observe(ref.current);
    return () => ro.disconnect();
  }, []);
  return <div ref={ref} className={className}>{ready ? children : null}</div>;
}

const PRICE_PERIODS = [
  { v: "day", l: "Daily" },
  { v: "week", l: "Weekly" },
  { v: "month", l: "Monthly" },
];
const SALES_PERIODS = [
  { v: "day", l: "Daily" },
  { v: "week", l: "Weekly" },
  { v: "month", l: "Monthly" },
  { v: "quarter", l: "Quarterly" },
  { v: "half_year", l: "Half-yearly" },
  { v: "year", l: "Yearly" },
];

const COLORS = ["#1B4332", "#E07A5F", "#40916C", "#B08968", "#3D5A80", "#7B2CBF"];

export default function AnalyticsPanel() {
  const [pricePeriod, setPricePeriod] = useState("day");
  const [salesPeriod, setSalesPeriod] = useState("month");
  const [productId, setProductId] = useState("all");
  const [products, setProducts] = useState([]);
  const [priceRows, setPriceRows] = useState([]);
  const [sales, setSales] = useState({ series: [], top_products: [], summary: {} });

  useEffect(() => {
    api.get("/products").then((r) => setProducts(r.data || []));
  }, []);

  useEffect(() => {
    const p = { period: pricePeriod };
    if (productId !== "all") p.product_id = productId;
    api.get("/analytics/price-trends", { params: p }).then((r) => setPriceRows(r.data || []));
  }, [pricePeriod, productId]);

  useEffect(() => {
    api.get("/analytics/sales", { params: { period: salesPeriod } }).then((r) => setSales(r.data));
  }, [salesPeriod]);

  // Pivot price rows: [{bucket, [productName]: price, ...}]
  const priceChartData = useMemo(() => {
    const byBucket = {};
    const nameSet = new Set();
    priceRows.forEach((r) => {
      const b = byBucket[r.bucket] || { bucket: r.bucket };
      b[r.product_name] = r.price;
      nameSet.add(r.product_name);
      byBucket[r.bucket] = b;
    });
    const arr = Object.values(byBucket).sort((a, b) => (a.bucket > b.bucket ? 1 : -1));
    return { arr, names: Array.from(nameSet) };
  }, [priceRows]);

  const kpis = [
    { label: "Revenue", value: `₹${Number(sales.summary?.revenue || 0).toLocaleString()}` },
    { label: "Orders", value: sales.summary?.orders || 0 },
    { label: "Units sold", value: Number(sales.summary?.quantity || 0).toFixed(2) },
    { label: "Products", value: products.length },
  ];

  return (
    <div className="space-y-8">
      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="analytics-kpis">
        {kpis.map((k) => (
          <Card key={k.label} className="border-black/10">
            <CardContent className="p-5">
              <div className="text-xs uppercase tracking-[0.24em] text-charcoal/50">{k.label}</div>
              <div className="mt-2 font-serif text-3xl text-forest">{k.value}</div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Price trends */}
      <Card className="border-black/10">
        <CardContent className="p-6">
          <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
            <div>
              <h3 className="text-lg font-semibold text-charcoal">Price trends</h3>
              <p className="text-xs text-charcoal/60">Last recorded price per bucket</p>
            </div>
            <div className="flex gap-2">
              <Select value={productId} onValueChange={setProductId}>
                <SelectTrigger className="w-[200px]" data-testid="price-product-filter">
                  <SelectValue placeholder="All products" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All products</SelectItem>
                  {products.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                </SelectContent>
              </Select>
              <Select value={pricePeriod} onValueChange={setPricePeriod}>
                <SelectTrigger className="w-[140px]" data-testid="price-period-filter"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {PRICE_PERIODS.map((p) => <SelectItem key={p.v} value={p.v}>{p.l}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="h-[320px] w-full" data-testid="price-chart">
            {priceChartData.arr.length === 0 ? (
              <div className="h-full flex items-center justify-center text-charcoal/50 text-sm">
                No price history yet. Update a product price to build a trend.
              </div>
            ) : (
              <ChartMount className="h-full w-full">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={priceChartData.arr}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e2dc" />
                    <XAxis dataKey="bucket" tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 11 }} />
                    <Tooltip />
                    <Legend wrapperStyle={{ fontSize: 12 }} />
                    {priceChartData.names.slice(0, 6).map((n, i) => (
                      <Line key={n} type="monotone" dataKey={n} stroke={COLORS[i % COLORS.length]} strokeWidth={2} dot />
                    ))}
                  </LineChart>
                </ResponsiveContainer>
              </ChartMount>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Sales report */}
      <Card className="border-black/10">
        <CardContent className="p-6">
          <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
            <div>
              <h3 className="text-lg font-semibold text-charcoal">Sales report</h3>
              <p className="text-xs text-charcoal/60">Paid orders only</p>
            </div>
            <Select value={salesPeriod} onValueChange={setSalesPeriod}>
              <SelectTrigger className="w-[160px]" data-testid="sales-period-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                {SALES_PERIODS.map((p) => <SelectItem key={p.v} value={p.v}>{p.l}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="h-[320px] w-full" data-testid="sales-chart">
            {sales.series?.length === 0 ? (
              <div className="h-full flex items-center justify-center text-charcoal/50 text-sm">
                No paid orders yet.
              </div>
            ) : (
              <ChartMount className="h-full w-full">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={sales.series}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e2dc" />
                    <XAxis dataKey="bucket" tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 11 }} />
                    <Tooltip />
                    <Legend wrapperStyle={{ fontSize: 12 }} />
                    <Bar dataKey="revenue" fill="#1B4332" name="Revenue (₹)" radius={[4, 4, 0, 0]} />
                    <Bar dataKey="orders" fill="#E07A5F" name="Orders" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </ChartMount>
            )}
          </div>

          {sales.top_products?.length > 0 && (
            <div className="mt-8">
              <h4 className="text-sm font-semibold text-charcoal mb-3">Top-selling products</h4>
              <div className="border border-black/10 rounded-md overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-[#F4F4F5] text-charcoal/70">
                    <tr>
                      <th className="text-left px-4 py-2 font-medium">Product</th>
                      <th className="text-right px-4 py-2 font-medium">Qty sold</th>
                      <th className="text-right px-4 py-2 font-medium">Orders</th>
                      <th className="text-right px-4 py-2 font-medium">Revenue</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sales.top_products.slice(0, 10).map((t) => (
                      <tr key={t.product_id} className="border-t border-black/10">
                        <td className="px-4 py-2">{t.name}</td>
                        <td className="px-4 py-2 text-right font-mono">{t.quantity.toFixed(2)} {t.uom}</td>
                        <td className="px-4 py-2 text-right font-mono">{t.orders}</td>
                        <td className="px-4 py-2 text-right font-mono">₹{t.revenue.toFixed(2)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
